// src/encounter/lib/order-category.ts
//
// Lab vs imaging on the shared `radiologyOrderRequest` list (PROD-2619).
//
// Labs and imaging live in ONE table (ScripsPatient.dbo.ServiceOrderRequest); the category is the
// only thing that tells them apart. React writes exactly what Flutter writes
// (app_constants.dart:252-255):
//   lab     → categoryCode '108252007' / categoryName 'laboratory'
//   imaging → categoryCode '363679005' / categoryName 'imaging'
// The backend keys on those values: a PatientLabRequest is created on sign only when
// CategoryName == "laboratory" (AssessmentPlanConditionService.cs:536), and the post-visit summary,
// Orders service and Rule engine filter on 'laboratory' / 'imaging'
// (PatientAssessmentPlanRepository.cs:312, 332, 351, 371).
//
// Between PROD-1297 (June 2026) and PROD-2619, React wrote 'lab'/'lab' and 'radiology'/'radiology'.
// Those rows still exist, so the reader accepts them too. PURE — no I/O.
import type { AssessmentRadiology } from '@/types/assessment-api';

/** SNOMED 108252007 "Laboratory procedure" — Flutter's lab category code. */
export const LAB_CATEGORY_CODE = '108252007';
export const LAB_CATEGORY_NAME = 'laboratory';
/** SNOMED 363679005 "Imaging" — Flutter's imaging category code. */
export const IMAGING_CATEGORY_CODE = '363679005';
export const IMAGING_CATEGORY_NAME = 'imaging';

/** Legacy React values (PROD-1297 → PROD-2619). Read-only: never written any more. */
const LEGACY_LAB = 'lab';
const LEGACY_RADIOLOGY = 'radiology';

const LAB_VALUES = new Set([LAB_CATEGORY_CODE, LAB_CATEGORY_NAME, LEGACY_LAB]);
const IMAGING_VALUES = new Set([IMAGING_CATEGORY_CODE, IMAGING_CATEGORY_NAME, LEGACY_RADIOLOGY]);

export type RadiologyRowKind = 'lab' | 'imaging';

/** The category pair a lab or imaging order is written with. */
export function categoryFor(kind: RadiologyRowKind): { categoryCode: string; categoryName: string } {
  return kind === 'lab'
    ? { categoryCode: LAB_CATEGORY_CODE, categoryName: LAB_CATEGORY_NAME }
    : { categoryCode: IMAGING_CATEGORY_CODE, categoryName: IMAGING_CATEGORY_NAME };
}

function norm(v: string | null | undefined): string {
  return (v ?? '').trim().toLowerCase();
}

/**
 * Classify one `radiologyOrderRequest` row as lab or imaging. The code decides first, like Flutter
 * (`isRadiology => categoryCode == '363679005'`, patient_assessment_model.dart:1951), accepting the
 * legacy React code too; then the name. A row with neither recognised is a lab when it carries
 * sub-tests (only labs do) and imaging otherwise.
 */
export function classifyRadiologyRow(
  row: Pick<AssessmentRadiology, 'categoryCode' | 'categoryName' | 'labOrderValueSetTestDetails'>,
): RadiologyRowKind {
  const code = norm(row.categoryCode);
  if (IMAGING_VALUES.has(code)) return 'imaging';
  if (LAB_VALUES.has(code)) return 'lab';
  const name = norm(row.categoryName);
  if (IMAGING_VALUES.has(name)) return 'imaging';
  if (LAB_VALUES.has(name)) return 'lab';
  return (row.labOrderValueSetTestDetails?.length ?? 0) > 0 ? 'lab' : 'imaging';
}
